import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { useSearchParams } from 'react-router-dom';
import { Building2, Star, MapPin, Loader2, Info, PlugZap, Search, Filter } from 'lucide-react';
import { format, parseISO, isValid, differenceInCalendarDays } from 'date-fns';
import SearchWidget from '../components/SearchWidget';
import IndicativeResultsNotice from '../components/IndicativeResultsNotice';
import CategoryNav from '../components/CategoryNav';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Slider } from '../components/ui/slider';
import { Label } from '../components/ui/label';
import { Separator } from '../components/ui/separator';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Canonical hotel search state is *always* driven by the URL query string.
// No sessionStorage fallback — refresh/back/forward will trust the URL only.
const readSearchFromParams = (params) => {
  const dest = params.get('dest') || '';
  const checkIn = params.get('checkIn') || '';
  const checkOut = params.get('checkOut') || '';
  if (!dest || !checkIn || !checkOut) return null;
  const ci = parseISO(checkIn);
  const co = parseISO(checkOut);
  if (!isValid(ci) || !isValid(co) || co <= ci) return null;
  return {
    dest,
    checkIn,
    checkOut,
    rooms: Math.max(1, Math.min(8, Number(params.get('rooms') || 1))),
    guests: Math.max(1, Math.min(16, Number(params.get('guests') || 2))),
  };
};

const HotelHero = ({ canonical }) => (
  <section className="bg-teal-800 pt-8 pb-24 relative">
    <div className="max-w-7xl mx-auto px-4 md:px-6 text-white">
      <div className="text-teal-200 uppercase tracking-widest text-xs font-semibold">Hotels</div>
      <h1 className="font-display font-extrabold text-2xl md:text-3xl" data-testid="hotels-heading">
        {canonical ? canonical.dest : 'Handpicked stays around the world'}
      </h1>
      <p className="text-white/80 text-sm mt-1">
        {canonical
          ? `${format(parseISO(canonical.checkIn), 'EEE, d MMM')} — ${format(parseISO(canonical.checkOut), 'EEE, d MMM')} · ${canonical.rooms} room${canonical.rooms>1?'s':''} · ${canonical.guests} guest${canonical.guests>1?'s':''}`
          : 'Free cancellation on most rooms · best price guarantee'}
      </p>
    </div>
  </section>
);

const NoActiveProviderCard = ({ providersUnavailable }) => {
  const visible = (providersUnavailable || []).filter(p =>
    !/sandbox_disabled_in_production|kayak/i.test(`${p.reason || ''} ${p.name || ''}`)
  );
  return (
  <Card className="border-slate-200" data-testid="hotels-no-provider">
    <CardContent className="p-10 md:p-14 text-center">
      <div className="w-14 h-14 rounded-full bg-teal-50 text-teal-700 flex items-center justify-center mx-auto">
        <PlugZap className="w-7 h-7" />
      </div>
      <div className="font-display font-extrabold text-xl text-slate-900 mt-4">
        No active hotel provider available yet
      </div>
      <p className="text-slate-600 text-sm mt-2 max-w-lg mx-auto">
        We're finalising integrations with our hotel partners. Live rates and rooms will appear
        here as soon as a provider is activated. In the meantime, you can still search flights
        and cars — no dummy hotels are ever shown here.
      </p>
      {visible.length > 0 && (
        <div className="mt-5 inline-flex flex-wrap gap-2 justify-center">
          {visible.map((p, i) => (
            <Badge key={i} variant="outline" className="border-slate-300 text-slate-600 text-[11px]">
              {p.name}: {p.reason}
            </Badge>
          ))}
        </div>
      )}
    </CardContent>
  </Card>
  );
};

const HotelResultCard = ({ h, nights }) => {
  const total = h.totalPrice ?? (h.pricePerNight != null ? h.pricePerNight * nights : null);
  return (
    <Card className="overflow-hidden border-slate-200 card-lift" data-testid="hotel-card">
      <div className="relative aspect-[4/3] overflow-hidden bg-slate-100">
        {h.image
          ? <img src={h.image} alt={h.name} className="absolute inset-0 w-full h-full object-cover" />
          : <div className="absolute inset-0 flex items-center justify-center text-slate-400"><Building2 className="w-10 h-10" /></div>
        }
        {h.starRating != null && (
          <Badge className="absolute top-3 left-3 bg-teal-700 text-white border-0 gap-1">
            <Star className="w-3 h-3 fill-white" /> {h.starRating}
          </Badge>
        )}
      </div>
      <CardContent className="p-4">
        <div className="flex items-center text-xs text-slate-500 gap-1"><MapPin className="w-3 h-3" /> {h.city || h.address || '—'}</div>
        <div className="font-display font-bold text-slate-900 mt-1 truncate" title={h.name}>{h.name}</div>
        <div className="text-xs text-slate-500 mt-0.5">
          {h.guestRating != null ? `${h.guestRating} guest rating` : ''}
          {h.reviewCount ? ` · ${h.reviewCount.toLocaleString()} reviews` : ''}
        </div>
        {h.amenities?.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-3">
            {h.amenities.slice(0, 3).map(a => (
              <Badge key={a} variant="outline" className="border-slate-300 text-slate-700">{a}</Badge>
            ))}
          </div>
        )}
        <div className="flex items-end justify-between mt-4">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-slate-500">per night</div>
            <div className="font-display font-extrabold text-xl text-teal-700">
              {h.pricePerNight != null ? `${h.currency} ${Math.round(h.pricePerNight)}` : '—'}
            </div>
            {total != null && (
              <div className="text-[11px] text-slate-500">Total {h.currency} {Math.round(total)}</div>
            )}
          </div>
          <a
            href={h.deeplink || '#'}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center h-10 px-4 rounded-md bg-amber-500 hover:bg-amber-600 text-slate-900 font-bold text-sm"
            data-testid="hotel-view-deal"
          >
            View deal
          </a>
        </div>
        <div className="mt-2 text-[10px] text-slate-500">via {h.providerName}</div>
      </CardContent>
    </Card>
  );
};

export default function Hotels() {
  const [params] = useSearchParams();
  const canonical = useMemo(() => readSearchFromParams(params), [params]);
  const [status, setStatus] = useState('idle'); // idle | loading | complete | no_provider | error
  const [results, setResults] = useState([]);
  const [providers, setProviders] = useState({ used: [], unavailable: [] });
  const [sandbox, setSandbox] = useState(false);
  const [sort, setSort] = useState('recommended');
  const [maxPrice, setMaxPrice] = useState([1500]);
  const abortRef = useRef(null);

  const nights = useMemo(() => {
    if (!canonical) return 1;
    return Math.max(1, differenceInCalendarDays(parseISO(canonical.checkOut), parseISO(canonical.checkIn)));
  }, [canonical]);

  useEffect(() => {
    // Whenever the canonical URL state changes, cancel any in-flight request
    // and start a fresh search. This guarantees no stale cross-search leaks.
    if (abortRef.current) abortRef.current.abort();
    if (!canonical) {
      setStatus('idle');
      setResults([]);
      setProviders({ used: [], unavailable: [] });
      return;
    }
    const controller = new AbortController();
    abortRef.current = controller;
    setStatus('loading');
    setResults([]);
    (async () => {
      try {
        const body = {
          destination: canonical.dest,
          checkIn: canonical.checkIn,
          checkOut: canonical.checkOut,
          rooms: canonical.rooms,
          guests: canonical.guests,
        };
        const { data } = await axios.post(`${API}/hotels/search`, body, { signal: controller.signal });
        if (controller.signal.aborted) return;
        setResults(Array.isArray(data.results) ? data.results : []);
        setProviders(data.providers || { used: [], unavailable: [] });
        setSandbox(!!data.sandbox);
        setStatus(data.status === 'complete' ? 'complete' : 'no_provider');
      } catch (e) {
        if (controller.signal.aborted || axios.isCancel(e)) return;
        setStatus('error');
      }
    })();
    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canonical?.dest, canonical?.checkIn, canonical?.checkOut, canonical?.rooms, canonical?.guests]);

  const filtered = useMemo(() => {
    let list = results.filter(r => r.pricePerNight == null || r.pricePerNight <= maxPrice[0]);
    if (sort === 'price') list = [...list].sort((a, b) => (a.pricePerNight ?? 9e9) - (b.pricePerNight ?? 9e9));
    if (sort === 'rating') list = [...list].sort((a, b) => (b.guestRating ?? 0) - (a.guestRating ?? 0));
    return list;
  }, [results, maxPrice, sort]);

  return (
    <div>
      <HotelHero canonical={canonical} />
      <div className="max-w-7xl mx-auto px-4 md:px-6 -mt-16 relative z-10">
        {/* Cheapora category nav — Flights routes back to homepage TP WL. */}
        <CategoryNav />
        <SearchWidget defaultTab="hotels" compact initial={canonical} key={params.toString() || 'landing'} />
      </div>

      {!canonical && (
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-14 text-center">
          <Search className="w-8 h-8 text-teal-700 mx-auto" />
          <div className="font-display font-bold text-slate-900 mt-3">Start your hotel search</div>
          <p className="text-slate-600 text-sm mt-1 max-w-md mx-auto">
            Enter a destination and dates above. We'll compare live rooms across our activated
            provider partners.
          </p>
        </div>
      )}

      {canonical && (
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-10 grid grid-cols-1 lg:grid-cols-4 gap-6">
          <aside className="lg:col-span-1">
            <Card className="border-slate-200 sticky top-20">
              <CardContent className="p-5">
                <div className="flex items-center gap-2 mb-4">
                  <Filter className="w-4 h-4 text-teal-700" />
                  <div className="font-display font-bold text-slate-900">Filters</div>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-widest text-slate-500">Max price / night</Label>
                  <Slider value={maxPrice} onValueChange={setMaxPrice} min={50} max={2000} step={25} className="mt-3" data-testid="hotel-price-slider" />
                  <div className="text-sm mt-2 text-slate-700">Up to <span className="font-bold text-teal-700">S${maxPrice[0]}</span></div>
                </div>
                <Separator className="my-5" />
                <div className="text-xs text-slate-500 leading-relaxed">
                  Filters activate as soon as a hotel provider is connected. Nothing is
                  fabricated here in the meantime.
                </div>
              </CardContent>
            </Card>
          </aside>

          <div className="lg:col-span-3">
            <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
              <div className="text-sm text-slate-600 flex items-center gap-2 flex-wrap">
                {status === 'loading' && (<><Loader2 className="w-4 h-4 animate-spin text-teal-700" /> Searching partner inventory…</>)}
                {status === 'complete' && (<>{filtered.length} of {results.length} stays found</>)}
                {status === 'no_provider' && (<>0 stays · No active hotel provider yet</>)}
                {status === 'error' && (<>Couldn't fetch stays right now.</>)}
              </div>
              <Tabs value={sort} onValueChange={setSort}>
                <TabsList>
                  <TabsTrigger value="recommended">Recommended</TabsTrigger>
                  <TabsTrigger value="price">Cheapest</TabsTrigger>
                  <TabsTrigger value="rating">Guest rating</TabsTrigger>
                </TabsList>
              </Tabs>
            </div>

            {status === 'loading' && (
              <Card className="border-slate-200">
                <CardContent className="p-10 text-center text-slate-500 flex items-center justify-center gap-2">
                  <Loader2 className="w-5 h-5 animate-spin text-teal-700" /> Fetching partner inventory…
                </CardContent>
              </Card>
            )}
            {status === 'complete' && filtered.length > 0 && (
              <>
                <IndicativeResultsNotice />
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5" data-testid="hotels-results-grid">
                  {filtered.map(h => <HotelResultCard key={h.id} h={h} nights={nights} />)}
                </div>
              </>
            )}
            {status === 'complete' && filtered.length === 0 && (
              <Card className="border-slate-200" data-testid="hotels-empty-filters">
                <CardContent className="p-10 text-center text-slate-500">
                  No stays match your filters. Try increasing the max price.
                </CardContent>
              </Card>
            )}
            {status === 'no_provider' && (
              <Card className="border-slate-200" data-testid="hotels-no-provider">
                <CardContent className="p-10 md:p-14 text-center">
                  <div className="font-display font-extrabold text-xl text-slate-900">
                    No indicative results are currently available for this destination
                  </div>
                  <p className="text-slate-600 text-sm mt-2 max-w-lg mx-auto">
                    Please try another route or destination. Live booking availability is coming
                    soon as we finalise our provider integrations.
                  </p>
                </CardContent>
              </Card>
            )}
            {status === 'error' && (
              <Card className="border-slate-200">
                <CardContent className="p-10 text-center text-slate-500">
                  We couldn't fetch stays right now. Please try again in a moment.
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
